// Copyright (c) 2025 Audere, All Rights Reserved

import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Box, Typography, LinearProgress, Alert, Stack, IconButton } from "@mui/material";
import { generatePath, useLocation, useNavigate, useParams } from "react-router";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { useAddRDTMutation } from "../features/data/projectApiSlice";
import { createStyleSheet } from "../utils/styles";
import {
  PROJECT_FLOW_QUERY_KEYS,
  PROJECT_FLOW_STEP_VALUES,
  TEST_TYPES,
  type LineTestType,
  type addRDTPayload,
  type TestResults,
} from "../types/projects";
import { theme } from "../../theme";
import AddNewTestForm from "./AddNewTestForm";
import { getErrorText } from "../utils/error";
import { stringInputToArray } from "../utils/string";
import TestFileUpload from "./TestFileUpload";
import { useSelector } from "react-redux";
import { getNewTestFormValues, setNewTestFormValues } from "../features/data/projectSlice";
import { useAppDispatch } from "../features/store";
import {
  UPDATE_PROJECT_WITH_TEST_PATH,
  CREATE_PROJECT_WITH_TEST_PATH,
  UPDATE_PROJECT_WITHOUT_TEST_PATH,
  CREATE_PROJECT_WITHOUT_TEST_PATH,
} from "../utils/constants";

const AddNewTest = () => {
  const { t } = useTranslation("AddNewTest");
  const [lineTestType, setLineTestType] = useState<LineTestType>(TEST_TYPES.TWO_LINE);
  const [createNewTest, { isSuccess: isCreateSuccess, data: addRdtData, error: isCreateError }] =
    useAddRDTMutation();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useMemo(() => {
    return new URLSearchParams(location.search);
  }, [location.search]);
  const currentStep = searchParams.get(PROJECT_FLOW_QUERY_KEYS.STEP);
  const dispatch = useAppDispatch();
  const newTestFormValues = useSelector(getNewTestFormValues);
  const params = useParams();

  const methods = useForm<addRDTPayload>({
    mode: "onChange",
    defaultValues: {
      brand: "",
      model: "",
      condition: "",
      who_product_name: null,
      who_pq: null,
      results: {
        window0: {
          control: ["control", "c"],
          test0: "",
          test1: "",
        },
      },
      lineTestType: TEST_TYPES.TWO_LINE,
    },
  });
  const { watch, reset, setError } = methods;

  const buildResultWindow = useCallback(
    (formData: addRDTPayload): TestResults => {
      const { test0, test1 } = formData.results.window0;

      const result: TestResults = {
        control: ["control", "c"],
        test0: stringInputToArray(test0),
      };

      if (lineTestType === TEST_TYPES.THREE_LINE && test1) {
        result.test1 = stringInputToArray(test1);
      }

      return result;
    },
    [lineTestType]
  );

  const onSubmit = useCallback(
    async (formData: addRDTPayload) => {
      const trimLower = (val: string) => val.trim().toLowerCase();

      const payload = {
        brand: formData.brand.trim(),
        model: formData.model.trim(),
        condition: formData.condition.trim(),
        who_product_name: formData.who_product_name ?? null,
        who_pq: formData.who_pq ?? null,
        results: { window0: buildResultWindow(formData) },
      };

      const normalized = {
        brand: trimLower(formData.brand),
        model: trimLower(formData.model),
        condition: trimLower(formData.condition),
      };

      const restrictedValues = {
        brand: "standard",
        model: "standard",
        condition: "generic",
      };

      const isRestrictedCombo = Object.entries(restrictedValues).some(
        ([key, value]) => normalized[key as keyof typeof normalized] === value
      );

      if (isRestrictedCombo) {
        (Object.keys(restrictedValues) as (keyof typeof restrictedValues)[]).forEach(field => {
          if (normalized[field] === restrictedValues[field]) {
            setError(field, {
              type: "manual",
              message: field === "condition" ? t("genericError") : t("standardError"),
            });
          }
        });
        return;
      }

      createNewTest(payload);
    },
    [createNewTest, buildResultWindow, setError, t]
  );
  const handleCancel = () => {
    const { orgUid, projectUid } = params;
    searchParams.set(PROJECT_FLOW_QUERY_KEYS.STEP, PROJECT_FLOW_STEP_VALUES.AI_MODEL_SETUP);
    const path = generatePath(
      projectUid ? UPDATE_PROJECT_WITHOUT_TEST_PATH : CREATE_PROJECT_WITHOUT_TEST_PATH,
      {
        orgUid: orgUid ?? "",
        projectUid: projectUid ?? "",
      }
    );
    navigate(`${path}?${searchParams.toString()}`);
  };

  useEffect(() => {
    const { orgUid, projectUid, testUid } = params;
    if (isCreateSuccess && !testUid) {
      searchParams.set(PROJECT_FLOW_QUERY_KEYS.STEP, PROJECT_FLOW_STEP_VALUES.IMAGE_UPLOAD);
      const path = generatePath(
        projectUid ? UPDATE_PROJECT_WITH_TEST_PATH : CREATE_PROJECT_WITH_TEST_PATH,
        {
          orgUid: orgUid ?? "",
          projectUid: projectUid ?? "",
          testUid: addRdtData?.rdt_uid,
        }
      );
      navigate(`${path}?${searchParams.toString()}`);
    }
  }, [
    isCreateSuccess,
    location.pathname,
    location.search,
    searchParams,
    addRdtData,
    navigate,
    params,
  ]);
  useEffect(() => {
    const subscription = watch(value => {
      if (value) {
        const clonedValue = JSON.parse(JSON.stringify(value));
        dispatch(setNewTestFormValues(clonedValue as addRDTPayload));
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, dispatch]);
  useEffect(() => {
    const currentValues = watch();
    if (JSON.stringify(currentValues) !== JSON.stringify(newTestFormValues)) {
      if (newTestFormValues?.lineTestType) setLineTestType(newTestFormValues?.lineTestType);
      reset(newTestFormValues, { keepIsValid: false });
    }
  }, [newTestFormValues, reset, watch]);

  return (
    <Box data-testid="new-test">
      <Typography variant="h6" sx={styles.header}>
        {t("pageTitle")}
      </Typography>
      <Box sx={styles.progressContainer}>
        <LinearProgress
          variant="determinate"
          value={isCreateSuccess ? 100 : 50}
          sx={styles.progressBar}
        />
      </Box>
      <Box sx={styles.pageContainer}>
        <Stack direction="row" justifyContent="start" alignItems="center" gap={1} mb={2}>
          <IconButton onClick={handleCancel} size="small" sx={styles.backBtn} data-testid="back-button">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6">{t("testTitlePlaceholder")}</Typography>
        </Stack>
        {isCreateError && (
          <Alert severity="error" sx={styles.alertContainer}>
            {getErrorText(isCreateError)}
          </Alert>
        )}
        {currentStep === PROJECT_FLOW_STEP_VALUES.ADD_NEW_TESTS ? (
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <AddNewTestForm
                setLineTestType={setLineTestType}
                lineTestType={lineTestType}
                onCancel={handleCancel}
              />
            </form>
          </FormProvider>
        ) : (
          currentStep === PROJECT_FLOW_STEP_VALUES.IMAGE_UPLOAD && (
            <TestFileUpload onCancel={handleCancel} />
          )
        )}
      </Box>
    </Box>
  );
};

export default AddNewTest;

const styles = createStyleSheet({
  pageContainer: {
    width: "100%",
    background: theme.palette.common.white,
    padding: "30px",
  },
  header: {
    marginBottom: "20px",
    fontSize: "40px",
  },
  progressContainer: {
    width: "100%",
    position: "relative",
  },
  progressBar: {
    height: "4px",
    backgroundColor: theme.palette.grey[300],
    "& .MuiLinearProgress-bar": {
      backgroundColor: theme.palette.primary.main,
      transition: "width 0.5s ease-in-out",
    },
  },
  alertContainer: {
    my: 1,
  },
  backBtn: {
    color: "primary.main",
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: "action.hover",
    },
  },
});
